// Firebase Storage service for InstaWorthy
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, setDoc, collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { compressImage, generateFileName } from './imageUtils';

/**
 * Upload image to Firebase Storage with progress tracking
 * @param {File} file - Image file to upload
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback (progress, snapshot)
 * @param {Function} onError - Error callback
 * @returns {Promise<Object>} - Upload result with downloadURL and metadata
 */
export const uploadImage = async (file, userId, onProgress, onError) => {
    try {
        // Compress image before upload
        const compressedBlob = await compressImage(file);
        
        // Generate unique filename
        const fileName = generateFileName(userId, file.name);
        const storageRef = ref(storage, fileName);
        
        // Create upload task
        const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
            contentType: 'image/jpeg',
            customMetadata: {
                originalName: file.name,
                uploadedAt: new Date().toISOString(),
                userId: userId
            }
        });
        
        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (onProgress) {
                        onProgress(progress, snapshot);
                    }
                },
                (error) => {
                    console.error('Upload error:', error);
                    if (onError) {
                        onError(error);
                    }
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        const result = {
                            downloadURL,
                            fileName,
                            originalName: file.name,
                            size: compressedBlob.size,
                            uploadedAt: new Date(),
                            path: fileName
                        };
                        
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    } catch (error) {
        console.error('Compression error:', error);
        throw error;
    }
};

/**
 * Upload multiple images in batch
 * @param {File[]} files - Array of image files
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback (fileIndex, progress, totalProgress)
 * @param {Function} onComplete - Complete callback (fileIndex, result)
 * @param {Function} onError - Error callback (fileIndex, error)
 * @returns {Promise<Object[]>} - Array of upload results
 */
export const uploadImageBatch = async (files, userId, onProgress, onComplete, onError) => {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
        try {
            const result = await uploadImage(
                files[i],
                userId,
                (progress) => {
                    const totalProgress = ((i * 100) + progress) / files.length;
                    if (onProgress) {
                        onProgress(i, progress, totalProgress);
                    }
                },
                (error) => {
                    if (onError) {
                        onError(i, error);
                    }
                }
            );
            
            results.push(result);
            
            if (onComplete) {
                onComplete(i, result);
            }
            
        } catch (error) {
            console.error(`Error uploading file ${i}:`, error);
            results.push({ error: error.message, fileName: files[i].name });
        }
    }
    
    return results;
};

/**
 * Save upload session to Firestore
 * @param {string} userId - User ID
 * @param {Object[]} uploadResults - Array of upload results
 * @returns {Promise<string>} - Session ID
 */
export const saveUploadSession = async (userId, uploadResults) => {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    
    const sessionData = {
        sessionId,
        userId,
        images: uploadResults.filter(result => !result.error),
        totalImages: uploadResults.length,
        successCount: uploadResults.filter(result => !result.error).length,
        failureCount: uploadResults.filter(result => result.error).length,
        createdAt: new Date(),
        status: 'uploaded', // uploaded, training, complete, failed
        trainingJobId: null
    };
    
    try {
        await setDoc(doc(db, 'uploadSessions', sessionId), sessionData);
        
        // Update user document with latest session
        await updateDoc(doc(db, 'users', userId), {
            lastUploadSession: sessionId,
            lastUploadAt: new Date(),
            uploadSessions: arrayUnion(sessionId)
        });
        
        return sessionId;
    } catch (error) {
        console.error('Error saving upload session:', error);
        throw new Error('Failed to save upload session. Please check Firestore security rules.');
    }
};

/**
 * Update user training status
 * @param {string} userId - User ID
 * @param {string} status - Training status
 * @param {Object} metadata - Additional metadata
 */
export const updateTrainingStatus = async (userId, status, metadata = {}) => {
    try {
        await updateDoc(doc(db, 'users', userId), {
            trainingStatus: status,
            trainingMetadata: {
                ...metadata,
                updatedAt: new Date()
            }
        });
    } catch (error) {
        console.error('Error updating training status:', error);
        throw new Error('Failed to update training status. Please check Firestore security rules.');
    }
};

/**
 * Delete uploaded image
 * @param {string} filePath - File path in storage
 * @returns {Promise<void>}
 */
export const deleteImage = async (filePath) => {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
};

/**
 * Clean up failed uploads
 * @param {Object[]} uploadResults - Upload results to clean up
 */
export const cleanupFailedUploads = async (uploadResults) => {
    const failedUploads = uploadResults.filter(result => result.error && result.path);
    
    for (const result of failedUploads) {
        try {
            await deleteImage(result.path);
        } catch (error) {
            console.warn('Could not clean up failed upload:', result.path, error);
        }
    }
};

/**
 * Get existing upload sessions for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object[]>} - Array of upload sessions
 */
export const getUserUploadSessions = async (userId) => {
    try {
        const sessionsQuery = query(
            collection(db, 'uploadSessions'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(10) // Get the latest 10 sessions
        );
        
        const querySnapshot = await getDocs(sessionsQuery);
        const sessions = [];
        
        querySnapshot.forEach((doc) => {
            sessions.push({
                id: doc.id,
                ...doc.data()
            });
        });
        
        return sessions;
    } catch (error) {
        console.error('Error fetching upload sessions:', error);
        return [];
    }
};

/**
 * Get the latest successful upload session for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} - Latest upload session or null
 */
export const getLatestUploadSession = async (userId) => {
    try {
        console.log('🔍 Searching for upload sessions for user:', userId);
        
        // Simple query to avoid compound index requirement - just filter by userId
        const sessionsQuery = query(
            collection(db, 'uploadSessions'),
            where('userId', '==', userId)
        );
        
        const querySnapshot = await getDocs(sessionsQuery);
        
        if (querySnapshot.empty) {
            console.log('❌ No upload sessions found at all');
            return null;
        }
        
        // Filter and sort in memory to avoid compound index requirement
        const allSessions = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            allSessions.push({
                id: doc.id,
                ...data
            });
        });
        
        console.log('📁 All sessions found:', allSessions);
        
        // Filter for sessions with uploaded images (uploaded or training status) and sort by createdAt descending
        const sessionsWithImages = allSessions
            .filter(session => 
                (session.status === 'uploaded' || session.status === 'training') && 
                session.images && 
                session.images.length > 0
            )
            .sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                return dateB - dateA; // Most recent first
            });
        
        console.log('✅ Sessions with images found:', sessionsWithImages);
        
        if (sessionsWithImages.length === 0) {
            console.log('❌ No sessions with images found');
            return null;
        }
        
        // Return the most recent session with images
        return sessionsWithImages[0];
    } catch (error) {
        console.error('Error fetching latest upload session:', error);
        return null;
    }
};